import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, TrendingUp, FileCheck, CreditCard, BarChart3 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import SchulungsMatrix from '../components/SchulungsMatrix';

import { InhaberHeader } from './inhaber/InhaberHeader';
import { LicenseLowBanner } from './inhaber/LicenseLowBanner';
import { InhaberQuickStart } from './inhaber/InhaberQuickStart';
import { InhaberStatsBanner } from './inhaber/InhaberStatsBanner';
import ProUpgradeCard from './pro/ProUpgradeCard';
import { InhaberPendingRequests } from './inhaber/InhaberPendingRequests';
import { InhaberTeamCard } from './inhaber/InhaberTeamCard';
import { InhaberUpsellGrid } from './inhaber/InhaberUpsellGrid';
import { InhaberCourseDialog } from './inhaber/InhaberCourseDialog';
import { InhaberAddEmployeeDialog } from './inhaber/InhaberAddEmployeeDialog';
import { InhaberTeamProgress } from './inhaber/InhaberTeamProgress';
import { InhaberSubscriptionTab } from './inhaber/InhaberSubscriptionTab';
import TrainingWishForm from '../components/TrainingWishForm';
import CourseWishBanner from '../components/CourseWishBanner';
import { SimpleChefDashboard } from './inhaber/SimpleChefDashboard';
import TierUpgradePrompt from '../components/TierUpgradePrompt';
import LicenseUpgradeModal from '../components/LicenseUpgradeModal';
import { ReferralShareCard } from '../components/ReferralShareCard';
import { FeedbackWidget } from '../components/FeedbackWidget';

import { apiFetch } from '../services/api';
const API = process.env.REACT_APP_BACKEND_URL;

const TabButton = ({ id, label, icon: Icon, active, onClick }) => (
  <button
    onClick={() => onClick(id)}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
      active ? 'bg-yellow-500 text-black font-bold' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
    }`}
    data-testid={`tab-${id}`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

const InhaberDashboard = ({ company, onLogout }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showCourseDialog, setShowCourseDialog] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ name: '', email: '' });
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [subscription, setSubscription] = useState(null);
  const [actualEmployeeCount, setActualEmployeeCount] = useState(null);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState({ seatsUsed: 0, seatsTotal: 0 });
  const [teamProgress, setTeamProgress] = useState(null);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [licenseSyncing, setLicenseSyncing] = useState(false);
  const isAdmin = company?.email === 'elektrotechnik.seidel@outlook.com';

  useEffect(() => {
    loadDashboard();
    loadSubscription();
    loadTeamProgress();
    loadPricingTiers();
  }, []);

  // Rückkehr vom Digistore24-Upgrade: warten bis IPN das neue Kontingent meldet
  useEffect(() => {
    const expected = parseInt(sessionStorage.getItem('sv_license_expected') || '0', 10);
    if (!expected) return;
    setLicenseSyncing(true);
    let tries = 0;
    let timer;
    const check = async () => {
      tries += 1;
      try {
        const res = await apiFetch(`${API}/api/inhaber/dashboard`);
        const data = await res.json();
        if (data.success) {
          setDashboardData(data);
          if ((data.seats?.seats_total || 0) >= expected) {
            sessionStorage.removeItem('sv_license_expected');
            setLicenseSyncing(false);
            loadSubscription();
            toast({ title: 'Lizenz freigeschaltet', description: `Jetzt ${data.seats.seats_total} Lizenzen – laden Sie Ihren neuen Mitarbeiter ein.` });
            setShowAddEmployee(true);
            return;
          }
        }
      } catch { /* weiter prüfen */ }
      if (tries < 40) timer = setTimeout(check, 3000);
      else setLicenseSyncing(false);
    };
    check();
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  const loadPricingTiers = async () => {
    try {
      const response = await apiFetch(`${API}/api/pricing/tiers`);
      const data = await response.json();
      if (data.success) setPricingTiers(data.tiers || []);
    } catch (error) {
      console.error('Error loading pricing tiers:', error);
    }
  };

  const loadTeamProgress = async () => {
    try {
      const response = await apiFetch(`${API}/api/learning/inhaber/team-progress`);
      const data = await response.json();
      if (data.success) setTeamProgress(data);
    } catch (error) {
      console.error('Error loading team progress:', error);
    }
  };

  const loadSubscription = async () => {
    try {
      const response = await apiFetch(`${API}/api/subscriptions/current`, {
        headers: {} });
      const data = await response.json();
      if (data.success && data.hasSubscription) {
        setSubscription(data.subscription);
        if (typeof data.actual_employee_count === 'number') {
          setActualEmployeeCount(data.actual_employee_count);
        }
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await apiFetch(`${API}/api/inhaber/dashboard`, {
        headers: {} });
      const data = await response.json();
      if (data.success) setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      const response = await apiFetch(`${API}/api/inhaber/approve-request/${requestId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' } });
      const data = await response.json();
      if (data.success) {
        toast({ title: 'Erfolg', description: 'Kurs wurde freigeschaltet' });
        loadDashboard();
      }
    } catch (error) {
      toast({ title: 'Fehler', description: 'Konnte Anfrage nicht bearbeiten', variant: 'destructive' });
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      const response = await apiFetch(`${API}/api/inhaber/reject-request/${requestId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: '' }) });
      const data = await response.json();
      if (data.success) {
        toast({ title: 'Erfolg', description: 'Anfrage wurde abgelehnt' });
        loadDashboard();
      }
    } catch (error) {
      toast({ title: 'Fehler', description: 'Konnte Anfrage nicht bearbeiten', variant: 'destructive' });
    }
  };

  const handleActivateCourse = async (employeeId, courseId) => {
    try {
      const response = await apiFetch(`${API}/api/inhaber/activate-course`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId, courseId }) });
      const data = await response.json();
      if (response.ok && data.success) {
        toast({ title: 'Erfolg', description: 'Kurs wurde freigeschaltet' });
        // Refresh & re-sync selected employee
        const refreshed = await apiFetch(`${API}/api/inhaber/dashboard`, {
          headers: {} });
        const refreshedData = await refreshed.json();
        if (refreshedData.success) {
          setDashboardData(refreshedData);
          if (selectedEmployee) {
            const updated = (refreshedData.employees || []).find((e) => e.id === selectedEmployee.id);
            if (updated) setSelectedEmployee(updated);
          }
        }
      } else if (response.status === 402) {
        const detail = data.detail || {};
        const pkg = detail.package_type;
        toast({
          title: 'Premium-Paket erforderlich',
          description: detail.message || 'Bitte buchen Sie das passende Paket.',
          variant: 'destructive' });
        if (pkg) openCheckout(pkg);
      } else {
        toast({
          title: 'Fehler',
          description: data.detail?.message || data.detail || 'Fehler beim Freischalten',
          variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Fehler', description: 'Fehler beim Freischalten', variant: 'destructive' });
    }
  };

  const openCheckout = async (packageType) => {
    try {
      const res = await apiFetch(`${API}/api/learning/checkout/${packageType}`);
      const data = await res.json();
      if (data.success && data.checkout_url) {
        window.open(data.checkout_url, '_blank');
      } else {
        toast({
          title: 'Checkout nicht verfügbar',
          description: data.message || 'Bitte später erneut versuchen.',
          variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Fehler', description: 'Checkout konnte nicht geöffnet werden', variant: 'destructive' });
    }
  };

  const handleDeleteEmployee = async (emp) => {
    if (!emp) return;
    if (!window.confirm(`Möchten Sie ${emp.name} wirklich entfernen? Die Lizenz wird sofort wieder frei.`)) return;
    try {
      const response = await apiFetch(`${API}/api/employees/${emp.id}`, {
        method: 'DELETE',
        headers: {} });
      const data = await response.json();
      if (response.ok && data.success) {
        toast({ title: 'Mitarbeiter entfernt', description: data.message || 'Lizenz steht wieder zur Verfügung.' });
        loadDashboard();
      } else {
        toast({ title: 'Fehler', description: data.detail?.message || data.detail || 'Konnte Mitarbeiter nicht entfernen.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Fehler', description: 'Netzwerkfehler beim Entfernen.', variant: 'destructive' });
    }
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.name || !newEmployee.email) {
      toast({ title: 'Fehler', description: 'Bitte alle Felder ausfüllen', variant: 'destructive' });
      return;
    }
    try {
      const response = await apiFetch(`${API}/api/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEmployee) });
      const data = await response.json();
      if (response.ok && data.success) {
        toast({ title: 'Erfolg', description: 'Mitarbeiter wurde hinzugefügt – Willkommens-Mail wird verschickt.' });
        setNewEmployee({ name: '', email: '' });
        setShowAddEmployee(false);
        loadDashboard();
      } else if (response.status === 402) {
        const detail = data.detail || {};
        const seatsUsed = detail.seats_used ?? 0;
        const seatsTotal = detail.seats_total ?? 0;
        const currentTier = subscription?.tier;
        // Tier-Upgrade-Prompt zeigen, wenn Flex/Pro-Limit erreicht ist
        if (detail.action === 'seats_exhausted' && (currentTier === 'flex' || currentTier === 'pro')) {
          setUpgradeContext({ seatsUsed, seatsTotal });
          setShowUpgradePrompt(true);
        } else if (detail.action === 'seats_exhausted' && seatsTotal > 0) {
          setUpgradeContext({ seatsUsed, seatsTotal });
          setShowLicenseModal(true);
        } else {
          toast({
            title: 'Kein aktives Abo',
            description: detail.message || 'Bitte zuerst den Testzugang starten.',
            variant: 'destructive' });
          openCheckout('basis');
        }
        setShowAddEmployee(false);
      } else {
        toast({ title: 'Fehler', description: data.detail?.message || data.detail || 'Fehler beim Hinzufügen', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Fehler', description: 'Fehler beim Hinzufügen', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  const stats = dashboardData?.statistics || {};
  const employees = dashboardData?.employees || [];
  const pendingRequests = dashboardData?.pendingRequests || [];
  const notifications = dashboardData?.notifications || [];
  const allCourses = dashboardData?.allCourses || [];
  const freeTrialModuleId = dashboardData?.freeTrialModuleId || 'dguv-v1';
  const hasPaidSubscription = !!dashboardData?.hasPaidSubscription;
  const activatedPackageModules = dashboardData?.activatedPackageModules || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900">
      <InhaberHeader
        company={company}
        subscription={subscription ? { ...subscription, actual_employee_count: actualEmployeeCount ?? stats.totalEmployees } : null}
        seatsInfo={dashboardData?.seats}
        notifications={notifications}
        onLogout={onLogout}
      />

      <main className="container mx-auto px-4 py-8">
        <LicenseLowBanner seats={dashboardData?.seats} />
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <TabButton id="overview" label="Übersicht" icon={TrendingUp} active={activeTab === 'overview'} onClick={setActiveTab} />
          <TabButton id="progress" label="Kursfortschritt" icon={BookOpen} active={activeTab === 'progress'} onClick={setActiveTab} />
          <TabButton id="matrix" label="Schulungsmatrix" icon={FileCheck} active={activeTab === 'matrix'} onClick={setActiveTab} />
          <TabButton id="subscription" label="Abo & Zahlung" icon={CreditCard} active={activeTab === 'subscription'} onClick={setActiveTab} />
          {isAdmin && (
            <button
              onClick={() => navigate('/admin-analytics')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
              data-testid="admin-analytics-btn"
            >
              <BarChart3 className="w-4 h-4" /> Admin Analytics
            </button>
          )}
        </div>

        {activeTab === 'progress' && <InhaberTeamProgress teamProgress={teamProgress} />}

        {activeTab === 'subscription' && (
          <InhaberSubscriptionTab subscription={subscription} onOpenCheckout={openCheckout} />
        )}

        {activeTab === 'matrix' && <SchulungsMatrix companyId={company?.id} />}

        {activeTab === 'overview' && (
          <>
            <InhaberStatsBanner companyName={company?.name} stats={stats} />

            <SimpleChefDashboard
              employees={employees}
              stats={stats}
              onNavigateToTeam={() => setActiveTab('team')}
              onNavigateToCourses={(branche) => navigate(`/kurse?branche=${branche}`)}
            />

            <CourseWishBanner variant="cockpit" />

            <ProUpgradeCard />

            {employees.length === 0 && (
              <InhaberQuickStart
                onAddEmployee={() => setShowAddEmployee(true)}
                onOpenBasisCheckout={() => openCheckout('basis')}
              />
            )}

            <InhaberPendingRequests
              requests={pendingRequests}
              onApprove={handleApproveRequest}
              onReject={handleRejectRequest}
            />

            <InhaberTeamCard
              employees={employees}
              seats={dashboardData?.seats || {}}
              onAddEmployee={() => setShowAddEmployee(true)}
              onManageCourses={(emp) => {
                setSelectedEmployee(emp);
                setShowCourseDialog(true);
              }}
              onDeleteEmployee={handleDeleteEmployee}
              onOpenCheckout={openCheckout}
              licenseSyncing={licenseSyncing}
              onUpgradeTier={({ used, total }) => {
                const currentTier = subscription?.tier;
                setUpgradeContext({ seatsUsed: used, seatsTotal: total });
                if (currentTier === 'flex' || currentTier === 'pro') {
                  setShowUpgradePrompt(true);
                  return true;
                }
                if (total > 0) {
                  setShowLicenseModal(true);
                  return true;
                }
                return false;
              }}
            />

            <InhaberUpsellGrid
              allCourses={allCourses}
              freeTrialModuleId={freeTrialModuleId}
              activatedPackageModules={activatedPackageModules}
              onOpenCheckout={openCheckout}
            />

            <ReferralShareCard />

            <TrainingWishForm isAuthenticated={true} className="mt-6" />
          </>
        )}
      </main>

      <FeedbackWidget />

      <InhaberAddEmployeeDialog
        open={showAddEmployee}
        onOpenChange={setShowAddEmployee}
        value={newEmployee}
        onChange={setNewEmployee}
        onSubmit={handleAddEmployee}
      />

      <InhaberCourseDialog
        open={showCourseDialog}
        onOpenChange={setShowCourseDialog}
        selectedEmployee={selectedEmployee}
        allCourses={allCourses}
        freeTrialModuleId={freeTrialModuleId}
        activatedPackageModules={activatedPackageModules}
        hasPaidSubscription={hasPaidSubscription}
        onActivate={handleActivateCourse}
        onOpenCheckout={openCheckout}
      />

      <TierUpgradePrompt
        open={showUpgradePrompt}
        onOpenChange={setShowUpgradePrompt}
        currentTier={subscription?.tier}
        checkoutUrl={(() => {
          const currentTier = subscription?.tier;
          const nextKey = currentTier === 'flex' ? 'pro' : currentTier === 'pro' ? 'enterprise' : null;
          if (!nextKey) return null;
          const next = pricingTiers.find((t) => t.key === nextKey);
          return next?.checkout_url || null;
        })()}
        seatsUsed={upgradeContext.seatsUsed}
        seatsTotal={upgradeContext.seatsTotal}
      />

      <LicenseUpgradeModal
        open={showLicenseModal}
        onOpenChange={setShowLicenseModal}
        seatsUsed={upgradeContext.seatsUsed}
        seatsTotal={upgradeContext.seatsTotal}
      />
    </div>
  );
};

export default InhaberDashboard;
